import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BoardConnection, Source } from '../../../types';
import { toEditState, withConfiguration, withEditedFields } from './Connections.helpers';
import {
  ConnectionEditState,
  ConnectionUpdateOptions,
} from './Connections.types';
import type {
  ConnectionViewModel,
  ConnectionViewModelProps,
} from './Connection.ViewModel.types';

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

/**
 * Applies to an in-progress edit what changed on the server between `before` and `after`:
 * the authorisation fields, and each configuration key whose saved value changed.
 */
const withServerChanges = (
  editing: ConnectionEditState,
  before: ConnectionEditState,
  after: ConnectionEditState,
): ConnectionEditState => {
  const beforeConfiguration = asRecord(before.configuration);
  const afterConfiguration = asRecord(after.configuration);
  const configuration = { ...asRecord(editing.configuration) };

  for (const key of new Set([...Object.keys(beforeConfiguration), ...Object.keys(afterConfiguration)])) {
    if (JSON.stringify(beforeConfiguration[key]) === JSON.stringify(afterConfiguration[key])) {
      continue;
    }

    if (key in afterConfiguration) {
      configuration[key] = afterConfiguration[key];
    } else {
      delete configuration[key];
    }
  }

  return {
    ...editing,
    profileName: after.profileName,
    profileId: after.profileId,
    isAuthorised: after.isAuthorised,
    message: after.message,
    disableUntil: after.disableUntil,
    disabledReason: after.disabledReason,
    configuration,
  };
};

export const useConnectionViewModel = ({
  boardId,
  boardsApi,
  allSources,
  initialConnection,
  saveConnection,
  deleteConnection,
  reload,
  loadErrorMessage,
  saveErrorMessage,
  invalidConfigurationMessage,
  confirmDeleteConnection,
  launchAuthorisation,
  setActionErrorMessage,
  setIsAuthorisingConnection,
}: ConnectionViewModelProps): ConnectionViewModel => {
  const [editingConnection, setEditingConnection] = useState<ConnectionEditState | null>(
    initialConnection ? toEditState(initialConnection) : null,
  );
  // Tracked alongside editingConnection instead of looked up from `connections` by id,
  // because a just-created connection may not exist yet in that list (it depends on a
  // board refetch racing with the render that opens the settings dialog for it).
  const [originalConnection, setOriginalConnection] = useState<BoardConnection | null>(
    initialConnection ?? null,
  );

  // reAuthoriseEditingConnection needs the current dirty state, but isDirty is
  // derived further down from these two. A ref keeps the callback stable.
  const isDirtyRef = useRef(false);

  // Defaults an editor fills in arrive in bursts (one per effect, one per list that
  // loads), so the saved connection is tracked synchronously here and saved one
  // request at a time, always with the newest defaults.
  const originalConnectionRef = useRef<BoardConnection | null>(initialConnection ?? null);
  // The save in flight for defaults, if any. A user's Save waits for it, so the
  // older defaults-only copy can never land after, and overwrite, the user's.
  const defaultsSaveRef = useRef<Promise<void> | null>(null);
  const hasNewerDefaultsRef = useRef(false);

  const replaceOriginalConnection = useCallback((connection: BoardConnection | null): void => {
    originalConnectionRef.current = connection;
    setOriginalConnection(connection);
  }, []);

  const saveDefaults = useCallback((): void => {
    if (defaultsSaveRef.current) {
      hasNewerDefaultsRef.current = true;
      return;
    }

    const run = async (): Promise<void> => {
      try {
        do {
          hasNewerDefaultsRef.current = false;
          const connection = originalConnectionRef.current;
          if (connection) {
            await saveConnection(connection);
          }
        } while (hasNewerDefaultsRef.current);
      } finally {
        defaultsSaveRef.current = null;
      }
    };

    defaultsSaveRef.current = run();
  }, [saveConnection]);

  /**
   * Folds a default the editor filled in into the saved connection and saves it, so the
   * connection loads what the editor shows without the user having to press Save.
   */
  const applyDefaultToSavedConnection = useCallback(
    (update: Partial<ConnectionEditState>, options: ConnectionUpdateOptions): void => {
      const saved = originalConnectionRef.current;
      if (!saved) {
        return;
      }

      const savedEditState = toEditState(saved);
      const configuration = options.applyDefault
        ? options.applyDefault(savedEditState.configuration)
        : savedEditState.configuration;
      // withEditedFields leaves the configuration alone; applyDefault handles that.
      const withFields = options.isDefault ? withEditedFields(saved, update) : saved;

      let next: BoardConnection;
      try {
        next = withConfiguration(withFields, configuration);
      } catch (error) {
        console.error('Failed to stringify connection configuration defaults', error);
        return;
      }

      if (JSON.stringify(toEditState(next)) === JSON.stringify(savedEditState)) {
        return;
      }

      replaceOriginalConnection(next);
      saveDefaults();
    },
    [replaceOriginalConnection, saveDefaults],
  );

  const getSourceById = useCallback(
    (sourceId: string): Source | undefined =>
      allSources.find((source) => source.id.toLowerCase() === sourceId.toLowerCase()),
    [allSources],
  );

  const editingSource = useMemo(() => {
    if (!editingConnection) {
      return null;
    }

    return getSourceById(editingConnection.sourceTypeId) || null;
  }, [editingConnection, getSourceById]);

  const openConnectionSettings = useCallback((connection: BoardConnection): void => {
    setActionErrorMessage('');
    replaceOriginalConnection(connection);
    setEditingConnection(toEditState(connection));
  }, [replaceOriginalConnection, setActionErrorMessage]);

  const updateEditingConnection = useCallback(
    (update: Partial<ConnectionEditState>, options?: ConnectionUpdateOptions): void => {
      setEditingConnection((previous) => {
        if (!previous) {
          return previous;
        }

        const next = { ...previous, ...update };
        if (options?.updateConfiguration) {
          next.configuration = options.updateConfiguration(previous.configuration);
        }

        return next;
      });

      if (options?.isDefault || options?.applyDefault) {
        applyDefaultToSavedConnection(update, options);
      }
    },
    [applyDefaultToSavedConnection],
  );

  const closeConnectionSettings = useCallback((): void => {
    setEditingConnection(null);
    replaceOriginalConnection(null);
    setActionErrorMessage('');
  }, [replaceOriginalConnection, setActionErrorMessage]);

  const saveEditingConnection = useCallback(async (): Promise<void> => {
    if (!editingConnection || !originalConnection) {
      return;
    }

    const configuration = editingConnection.configuration ?? {};
    let configurationJson: string | undefined;

    try {
      configurationJson = JSON.stringify(configuration, null, 2);
    } catch (error) {
      console.error('Failed to stringify connection configuration', error);
      setActionErrorMessage(invalidConfigurationMessage || saveErrorMessage || loadErrorMessage);
      return;
    }

    const updated: BoardConnection = {
      ...withEditedFields(originalConnection, editingConnection),
      configuration,
      configurationJson,
    };

    await defaultsSaveRef.current;

    const saved = await saveConnection(updated);
    if (saved) {
      closeConnectionSettings();
    }
  }, [
    closeConnectionSettings,
    editingConnection,
    originalConnection,
    invalidConfigurationMessage,
    loadErrorMessage,
    saveConnection,
    saveErrorMessage,
    setActionErrorMessage,
  ]);

  const deleteEditingConnection = useCallback(async (): Promise<void> => {
    if (!editingConnection || !originalConnection) {
      return;
    }

    const confirmed = confirmDeleteConnection
      ? await confirmDeleteConnection(originalConnection)
      : true;

    if (!confirmed) {
      return;
    }

    const deleted = await deleteConnection(editingConnection.id);
    if (deleted) {
      closeConnectionSettings();
    }
  }, [closeConnectionSettings, confirmDeleteConnection, deleteConnection, editingConnection, originalConnection]);

  const canReAuthoriseConnection = useCallback(
    (connection: BoardConnection): boolean =>
      !!getSourceById(connection.sourceTypeId)?.reAuthoriseUrl,
    [getSourceById],
  );

  /**
   * Re-runs a connection's authorisation flow, without it having to be the one open in the
   * settings form. The connections list reconnects a failed card straight from the card with this.
   *
   * @param connection The connection to re-authorise.
   * @returns `true` once authorisation succeeded and the board has been reloaded.
   */
  const runReAuthorisation = useCallback(
    async (connection: BoardConnection): Promise<boolean> => {
      if (!boardId || !launchAuthorisation) {
        return false;
      }

      const source = getSourceById(connection.sourceTypeId);
      if (!source?.reAuthoriseUrl) {
        return false;
      }

      const authorised = await launchAuthorisation({
        mode: 'reauthorise',
        url: source.reAuthoriseUrl
          .replace('{boardId}', boardId)
          .replace('{connectionId}', connection.id),
        source,
      });

      if (!authorised) {
        return false;
      }

      // The popup reports back reliably now, so refresh straight away rather
      // than asking the user to trigger it.
      await reload();
      return true;
    },
    [boardId, getSourceById, launchAuthorisation, reload],
  );

  const reAuthoriseConnection = useCallback(
    async (connection: BoardConnection): Promise<boolean> => {
      setIsAuthorisingConnection(true);
      try {
        return await runReAuthorisation(connection);
      } finally {
        setIsAuthorisingConnection(false);
      }
    },
    [runReAuthorisation, setIsAuthorisingConnection],
  );

  const reAuthoriseEditingConnection = useCallback(async (): Promise<void> => {
    if (!editingConnection || !originalConnection || !boardId) {
      return;
    }

    // The form stays blocked until it holds the refreshed connection, so source editors
    // never load with the half-authorised one or mount once with the old one.
    setIsAuthorisingConnection(true);
    try {
      const authorised = await runReAuthorisation(originalConnection);
      if (!authorised) {
        return;
      }

      const refreshedConnection = await boardsApi.getConnection(boardId, originalConnection.id);
      replaceOriginalConnection(refreshedConnection);

      // Re-seeding the form would discard whatever the user has typed, so only
      // do it when there is nothing in progress. Refreshing originalConnection
      // above is what clears the "needs authorisation" state either way.
      if (!isDirtyRef.current) {
        setEditingConnection(toEditState(refreshedConnection));
        return;
      }

      // With edits in progress, keep them but take what the authorisation changed: the
      // account, and any configuration the server rewrote (Dynamics-family sources save the
      // environment picked during sign-in). Otherwise Save would write the old values back.
      setEditingConnection((previous) =>
        previous
          ? withServerChanges(previous, toEditState(originalConnection), toEditState(refreshedConnection))
          : previous,
      );
    } finally {
      setIsAuthorisingConnection(false);
    }
  }, [
    boardId,
    boardsApi,
    editingConnection,
    originalConnection,
    replaceOriginalConnection,
    runReAuthorisation,
    setIsAuthorisingConnection,
  ]);

  const canReAuthoriseEditingConnection = useMemo(() => {
    if (!originalConnection) {
      return false;
    }

    return canReAuthoriseConnection(originalConnection);
  }, [originalConnection, canReAuthoriseConnection]);

  const isDirty = useMemo(() => {
    if (!editingConnection || !originalConnection) {
      return false;
    }

    const originalEditState = toEditState(originalConnection);

    return (
      editingConnection.name !== originalEditState.name ||
      editingConnection.colour !== originalEditState.colour ||
      editingConnection.defaultStatusId !== originalEditState.defaultStatusId ||
      editingConnection.archived !== originalEditState.archived ||
      editingConnection.manageTasksHere !== originalEditState.manageTasksHere ||
      editingConnection.defaultToTop !== originalEditState.defaultToTop ||
      editingConnection.tagName !== originalEditState.tagName ||
      editingConnection.newTaskDaysLimit !== originalEditState.newTaskDaysLimit ||
      JSON.stringify(editingConnection.configuration) !== JSON.stringify(originalEditState.configuration)
    );
  }, [editingConnection, originalConnection]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  return {
    editingConnection,
    editingSource,
    canReAuthoriseEditingConnection,
    isDirty,
    openConnectionSettings,
    closeConnectionSettings,
    updateEditingConnection,
    saveEditingConnection,
    deleteEditingConnection,
    reAuthoriseEditingConnection,
    canReAuthoriseConnection,
    reAuthoriseConnection,
  };
};
